import { useRef } from "react";
import { cn } from "../../lib/utils";
import SelectionHandles from "./SelectionHandles";
import { RulerCorner, TopRuler, LeftRuler } from "./Rulers";
import FormatCardsRow from "./FormatCardsRow";

const BOARD_W = 960;
const BOARD_H = 540;

export default function MarketingCanvas({
  boardRef, elements, selection, onSelect, onClear, onContextMenu, onMove,
}) {
  return (
    <main
      data-testid="md-canvas-area"
      onClick={(e) => {
        if (e.target.dataset.canvasBg === "1") onClear();
      }}
      className="flex-1 min-w-0 min-h-0 relative"
      style={{
        background: "var(--pl-bg-2)",
        display: "grid",
        gridTemplateColumns: "42px 1fr",
        gridTemplateRows: "34px 1fr",
        gridTemplateAreas: '"corner top" "left viewport"',
      }}
    >
      <RulerCorner />
      <TopRuler />
      <LeftRuler />
      <div
        data-testid="md-canvas-viewport"
        style={{ gridArea: "viewport", overflow: "auto", minWidth: 0, minHeight: 0 }}
      >
        <div
          data-canvas-bg="1"
          className="min-w-full min-h-full flex flex-col items-center"
          style={{ padding: "56px 72px 120px" }}
        >
          <article
            ref={boardRef}
            data-testid="marketing-board"
            aria-label="Marketing design canvas"
            className="relative"
            style={{
              width: BOARD_W,
              height: BOARD_H,
              borderRadius: 18,
              background:
                "radial-gradient(circle at 78% 16%, rgba(39,230,209,0.20), transparent 19%)," +
                "radial-gradient(circle at 16% 86%, rgba(185,255,47,0.18), transparent 23%)," +
                "linear-gradient(135deg, #001713 0%, #062b25 56%, #071b17 100%)",
              color: "white",
              boxShadow: "18px 18px 0 rgba(0,0,0,0.48), 0 34px 90px rgba(0,0,0,0.35)",
              overflow: "hidden",
              border: "1px solid rgba(39,230,209,0.22)",
            }}
          >
            <div
              data-canvas-bg="1"
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage:
                  "linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)," +
                  "linear-gradient(0deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
                backgroundSize: "36px 36px",
                opacity: 0.32,
              }}
            />
            {elements.map((el) => (
              <CanvasElement
                key={el.id}
                el={el}
                selected={selection.includes(el.id)}
                onSelect={onSelect}
                onContextMenu={onContextMenu}
                onMove={onMove}
              />
            ))}
          </article>

          {/* Format presets row */}
          <FormatCardsRow onSelect={() => {}} />
        </div>
      </div>
    </main>
  );
}

function CanvasElement({ el, selected, onSelect, onContextMenu, onMove }) {
  const ref = useRef(null);
  const drag = useRef(null);

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    if (el.locked) return;
    const additive = e.metaKey || e.ctrlKey || e.shiftKey;
    onSelect(el.id, additive);
    drag.current = { x: e.clientX, y: e.clientY, moved: false };
    try { ref.current.setPointerCapture(e.pointerId); } catch (_) {}
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    const dy = e.clientY - drag.current.y;
    if (Math.abs(dx) + Math.abs(dy) > 2) {
      drag.current.moved = true;
      drag.current.x = e.clientX;
      drag.current.y = e.clientY;
      onMove(el.id, dx, dy);
    }
  };
  const onPointerUp = () => { drag.current = null; };

  const baseStyle = {
    position: "absolute",
    cursor: el.locked ? "not-allowed" : "move",
    userSelect: "none",
    visibility: el.hidden ? "hidden" : "visible",
    ...el.style,
  };

  return (
    <div
      ref={ref}
      data-testid={`md-el-${el.id}`}
      data-element-id={el.id}
      data-type={el.type}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => onContextMenu(e, el.id)}
      style={baseStyle}
      className={cn(
        "transition-shadow",
        selected && "outline outline-2 outline-[#0db7ff] outline-offset-[3px]",
      )}
    >
      {el.html ? <span dangerouslySetInnerHTML={{ __html: el.html }} /> : el.text}
      {selected && <SelectionHandles />}
    </div>
  );
}
